import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import { embedService, SessionData } from '../../services/embedService';
import { buildMoonPayParamsFromSession } from '../../utils/moonPaySessionParams';
import PayCheckoutView from './PayCheckoutView';
import { PaymentMethodTab } from './payCheckoutTypes';

const normalizeStatus = (status: string | undefined): 'issued' | 'pending' | 'processing' | 'completed' | 'failed' => {
  const normalized = (status ?? '').trim().toLowerCase();
  if (normalized === 'completed' || normalized === 'payoutcompleted') return 'completed';
  if (normalized === 'failed' || normalized === 'expired' || normalized === 'cancelled') return 'failed';
  if (normalized === 'processing') return 'processing';
  if (normalized === 'pending') return 'pending';
  return 'issued';
};

const isTerminal = (status: string | undefined): boolean => {
  const normalized = normalizeStatus(status);
  return normalized === 'completed' || normalized === 'failed';
};

const EmbedPage: React.FC = () => {
  const { uniqueHash } = useParams<{ uniqueHash: string }>();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [methodTab, setMethodTab] = useState<PaymentMethodTab>('card');
  const [copied, setCopied] = useState(false);
  const [moonPayExtraParams, setMoonPayExtraParams] = useState<Record<string, string> | undefined>();
  const sessionStatus = sessionData?.status;

  const showNavbar = Boolean(localStorage.getItem('accessToken'));

  useEffect(() => {
    if (!sessionData) {
      setMoonPayExtraParams(undefined);
      return;
    }

    let cancelled = false;
    void buildMoonPayParamsFromSession(sessionData)
      .then((params) => {
        if (!cancelled) setMoonPayExtraParams(params);
      })
      .catch((err) => {
        console.error('MoonPay params failed', err);
        if (!cancelled) setMoonPayExtraParams(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionData]);

  useEffect(() => {
    document.title = 'ZK Gate · Complete Payment';

    const fetchSessionData = async () => {
      if (!uniqueHash?.trim()) {
        setError('Invalid session ID format');
        setLoading(false);
        return;
      }

      try {
        const data = await embedService.getSessionData(uniqueHash);
        setSessionData(data);
      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load payment session');
      } finally {
        setLoading(false);
      }
    };

    void fetchSessionData();
  }, [uniqueHash]);

  useEffect(() => {
    if (!uniqueHash || !sessionStatus || isTerminal(sessionStatus)) return;

    setPolling(true);
    const timer = window.setInterval(async () => {
      try {
        const refreshed = await embedService.getSessionData(uniqueHash);
        setSessionData(refreshed);
        if (isTerminal(refreshed.status)) {
          setPolling(false);
          window.clearInterval(timer);
        }
      } catch {
        // keep polling
      }
    }, 10000);

    return () => {
      setPolling(false);
      window.clearInterval(timer);
    };
  }, [uniqueHash, sessionStatus]);

  const refreshStatus = async () => {
    if (!uniqueHash) return;
    try {
      const refreshed = await embedService.getSessionData(uniqueHash);
      setSessionData(refreshed);
      setError(null);
    } catch {
      setError('Failed to refresh payment status');
    }
  };

  const copyWallet = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy wallet address');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center homeBg">
        <div className="text-center">
          <div className="spinner-border text-[#2563eb]" role="status" />
          <p className="mt-3 text-sm text-[#6b7280]">Loading payment session…</p>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="flex min-h-screen items-center justify-center homeBg px-4">
        <div className="max-w-md rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-lg">
          <h4 className="font-semibold text-red-700">Error</h4>
          <p className="mt-2 text-sm text-[#6b7280]">{error}</p>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="flex min-h-screen items-center justify-center homeBg px-4">
        <div className="max-w-md rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-lg">
          <p className="text-sm text-[#6b7280]">No session data available for this link.</p>
        </div>
      </div>
    );
  }

  const normalizedStatus = normalizeStatus(sessionData.status);
  const terminal = isTerminal(sessionData.status);

  return (
    <div className="min-h-screen homeBg">
      {showNavbar && <Navbar />}

      {error && (
        <div className="mx-auto max-w-[520px] px-4 pt-4">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
        </div>
      )}

      {terminal && (
        <div className="mx-auto max-w-[520px] px-4 pt-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Payment status: <strong>{normalizedStatus.toUpperCase()}</strong>
          </div>
        </div>
      )}

      <PayCheckoutView
        session={sessionData}
        methodTab={methodTab}
        onMethodTabChange={setMethodTab}
        terminal={terminal}
        moonPayExtraParams={moonPayExtraParams}
        walletAddress={sessionData.uniqueWalletAddress?.trim() ?? ''}
        checkoutUrl={sessionData.url?.trim() ?? ''}
        copied={copied}
        onCopyWallet={(address) => void copyWallet(address)}
        onRefreshStatus={() => void refreshStatus()}
        polling={polling}
        normalizedStatus={normalizedStatus}
      />
    </div>
  );
};

export default EmbedPage;
